/**
 * Diagnostic Runtime Toggle System
 * 
 * Single source of truth: Supabase user_settings.diagnostic_toggles (JSONB).
 * In-memory cache is populated on app boot and kept in sync.
 * React components use `useDiagnosticToggle()` hook for reactive updates.
 * Non-React code (main.tsx) uses `readDiagnosticRuntimeToggle()` which reads cache.
 */

export const DIAGNOSTIC_RUNTIME_TOGGLES = {
  ws_enabled: { defaultValue: false },
  preview_lazy_widgets: { defaultValue: false },
  preview_suspense_timeout: { defaultValue: true },
  preview_placeholder_system: { defaultValue: false },
  retry_lazy_widget: { defaultValue: true },
  global_retry_epoch: { defaultValue: true },
  widget_module_cache: { defaultValue: true },
  countdown_timer: { defaultValue: true },
  widget_diagnostic_panel: { defaultValue: true },
  reload_tracer: { defaultValue: true },
  chunk_recovery: { defaultValue: true },
  mobile_error_boundary: { defaultValue: true },
  startup_beacon: { defaultValue: true },
  widget_error_boundary: { defaultValue: true },
  progressive_widget_loading: { defaultValue: true },
  impersonation_debugger: { defaultValue: false },
  sentry_integration: { defaultValue: false },
  // Mobile Performance toggles
  mobile_polling_pause: { defaultValue: true },
  mobile_lazy_tab_mounting: { defaultValue: true },
  mobile_realtime_limiter: { defaultValue: true },
  mobile_list_virtualization: { defaultValue: true },
  mobile_refresh_override: { defaultValue: true },
  mobile_deferred_loading: { defaultValue: true },
  mobile_aggressive_code_split: { defaultValue: true },
  mobile_performance_guard: { defaultValue: true },
  mobile_news_tab_lite_mode: { defaultValue: true },
  mining_widget_diagnostics: { defaultValue: false },
} as const;

export type DiagnosticRuntimeToggleId = keyof typeof DIAGNOSTIC_RUNTIME_TOGGLES;

// ── In-memory cache ──
const cache: Record<string, boolean> = {};
let cacheLoaded = false;
let cacheSnapshot: Record<string, boolean> = {};
const listeners = new Set<() => void>();

/** Subscribe to cache changes (used by React hook) */
export function subscribeDiagnosticCache(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

function notifyListeners() {
  cacheSnapshot = { ...cache };
  listeners.forEach(cb => cb());
}

/** Bulk-set the cache from DB data. Called once on app boot. */
export function seedDiagnosticCache(dbToggles: Record<string, boolean>) {
  // Start with defaults
  for (const [id, config] of Object.entries(DIAGNOSTIC_RUNTIME_TOGGLES)) {
    cache[id] = config.defaultValue;
  }
  // Override with DB values
  for (const [key, value] of Object.entries(dbToggles)) {
    if (key in DIAGNOSTIC_RUNTIME_TOGGLES && typeof value === 'boolean') {
      cache[key] = value;
    }
  }
  cacheLoaded = true;
  notifyListeners();
}

/** Update a single toggle in cache (called by useDiagnosticToggles on toggle) */
export function updateDiagnosticCache(id: string, value: boolean) {
  cache[id] = value;
  notifyListeners();
}

/** Bulk update cache */
export function updateDiagnosticCacheBulk(updates: Record<string, boolean>) {
  for (const [key, value] of Object.entries(updates)) {
    cache[key] = value;
  }
  notifyListeners();
}

/** Synchronous read — returns cached value or default */
export function readDiagnosticRuntimeToggle(id: DiagnosticRuntimeToggleId): boolean {
  if (id in cache) return cache[id];
  const config = DIAGNOSTIC_RUNTIME_TOGGLES[id];
  return config ? config.defaultValue : true;
}

/** Whether the cache has been seeded from DB */
export function isDiagnosticCacheLoaded(): boolean {
  return cacheLoaded;
}

/** Get full snapshot of current cache (for React useSyncExternalStore) */
export function getDiagnosticCacheSnapshot(): Record<string, boolean> {
  return cacheSnapshot;
}
